import * as React from 'react';
import { AnyIcon } from '@liveauctioneers/hammer-ui-core/icons/anyIcon';
import { FileArchive, FileCsv, FilePdf, FileText, User } from '@liveauctioneers/hammer-ui-core/icons';
import { getFileExtensionName, getFileName } from '../../../../utils/filePath';
import { H6 } from '@liveauctioneers/hammer-ui-core/text';
import { longFormatDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { MessageType } from '../../types/Message';
import AttachmentLightbox from '../AttachmentLightbox/AttachmentLightbox';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import styled, { css } from 'styled-components';
import type { ImagesListType } from 'react-spring-lightbox';

type Props = {
    readonly message: MessageType;
    readonly testid?: string;
};

const FileIcons = {
    csv: FileCsv,
    doc: FileText,
    docx: FileText,
    numbers: FileText,
    pages: FileText,
    pdf: FilePdf,
    xls: FileText,
    xlsx: FileText,
    zip: FileArchive,
};

interface FileAttachment {
    readonly extension: keyof typeof FileIcons;
    readonly filename: string;
    readonly icon: AnyIcon;
    readonly url: string;
}

const Message = ({ message, testid = 'messengerMessage' }: Props) => {
    const [currentImageIndex, setCurrentImageIndex] = React.useState<number>(0);
    const [lightboxIsOpen, setLightboxIsOpen] = React.useState(false);
    const { attachments = [], messageBody, messageId, sender, tsSent } = message;

    const [fileAttachments, lightboxImages] = React.useMemo(() => {
        const files: FileAttachment[] = [];
        const images: ImagesListType = [];

        // Build separate lists of image + file attachments
        attachments.forEach(({ url }) => {
            const filename = getFileName(url);
            const extension = getFileExtensionName(url) as keyof typeof FileIcons;

            if (/\.(gif|jpe?g|tiff|png|webp|bmp)(\?.*)?$/i.test(filename)) {
                images.push({ alt: filename, src: url });
            } else {
                const icon = FileIcons[extension] || FileText; // use the known extension icon or the default
                files.push({ extension, filename, icon, url });
            }
        });

        return [files, images];
    }, [attachments]);

    const messageHasContent = messageBody?.replace(/<.+?>/g, '').trim().length > 0;

    if (!messageHasContent && attachments.length === 0) {
        return null;
    }

    return (
        <MessageListItem
            data-testid={testid}
            key={messageId}
        >
            <StyledAvatarContainer>
                {sender.type === 'bidder' ? (
                    <StyledAvatar>
                        <User style="fill" />
                    </StyledAvatar>
                ) : (
                    <StyledLogo>
                        <img
                            alt={sender.name}
                            loading="lazy"
                            src={sender.logoUrl}
                        />
                    </StyledLogo>
                )}
            </StyledAvatarContainer>
            <StyledMainConainer>
                <StyledName as="h6">{sender.name}</StyledName>
                <div className="hui-text-body-secondary text-text-secondary">{longFormatDate(tsSent)}</div>
                <MessageContent
                    $noContent={messageHasContent}
                    dangerouslySetInnerHTML={{
                        __html: messageHasContent ? messageBody : '',
                    }}
                />
                {Boolean(lightboxImages.length) && (
                    <ImageAttachmentsContainer>
                        <AttachmentLightbox
                            currentImageIndex={currentImageIndex}
                            images={lightboxImages}
                            isOpen={lightboxIsOpen}
                            onClose={() => setLightboxIsOpen(false)}
                            setImageIndex={setCurrentImageIndex}
                        />
                        {lightboxImages.map(({ alt, src }, i) => (
                            <ImageAttachmentSizeContainer key={i}>
                                <ImageAttachment
                                    alt={alt}
                                    onClick={() => {
                                        setCurrentImageIndex(i);
                                        setLightboxIsOpen(true);
                                    }}
                                    src={src}
                                />
                            </ImageAttachmentSizeContainer>
                        ))}
                    </ImageAttachmentsContainer>
                )}
                {Boolean(fileAttachments.length) && (
                    <FileAttachmentsContainer>
                        {fileAttachments.map(({ extension, icon: Icon, url }, i) => (
                            <Link
                                aria-label="open file"
                                key={i}
                                target="_blank"
                                to={url}
                            >
                                <ImageAttachmentSizeContainer>
                                    <Icon
                                        className="mb-2xs inline-block text-surface-500"
                                        size="md"
                                        style="fill"
                                    />
                                    {/* <StyledIcon icon={file.icon} /> */}
                                    <H6>{`${extension}`}</H6>
                                </ImageAttachmentSizeContainer>
                            </Link>
                        ))}
                    </FileAttachmentsContainer>
                )}
            </StyledMainConainer>
        </MessageListItem>
    );
};

export default Message;

const StyledAvatarContainer = styled.div`
    width: 46px;
    margin-right: 7px;
`;

const StyledName = styled(H6)`
    margin: 0;
`;

const StyledAvatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    background-color: ${({ theme }) => theme.colors.blue100};
    position: relative;

    && {
        svg {
            color: ${({ theme }) => theme.colors.white};
            height: 35px;
            width: 35px;
            position: absolute;
            bottom: -4px;
        }
    }
`;

const StyledLogo = styled.div`
    width: 40px;
    height: 40px;
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;

    img {
        max-height: 38px;
        max-width: 38px;
    }
`;

const StyledMainConainer = styled.div`
    width: calc(100% - 46px);
`;

const ImageAttachmentSizeContainer = styled.div`
    width: 120px;
    height: 120px;
    border: 1px solid ${({ theme }) => theme.colors.steel};
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin: 0 10px 10px 0;
    background-color: ${({ theme }) => theme.colors.grey500};
    text-transform: uppercase;
    text-decoration: none;
    h6 {
        color: ${({ theme }) => theme.colors.steel};
    }

    &:hover {
        border: 1px solid ${({ theme }) => theme.colors.blue200};

        h6 {
            color: ${({ theme }) => theme.colors.blue200};
        }

        svg {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }
`;

const ImageAttachment = styled.img`
    max-height: 118px;
    max-width: 118px;

    &:hover {
        cursor: pointer;
    }
`;

const ImageAttachmentsContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: 10px;

    a {
        text-decoration: none;
    }

    & > div:not(:last-child) {
        margin-right: 10px;
    }
`;
ImageAttachmentsContainer.displayName = 'ImageAttachmentsContainer';

const FileAttachmentsContainer = styled.div`
    ${ImageAttachmentsContainer}
`;
FileAttachmentsContainer.displayName = 'FileAttachmentsContainer';

const MessageContent = styled.div<{ $noContent?: boolean }>`
    ${({ $noContent }) =>
        $noContent
            ? css`
                  margin: 20px 0;
              `
            : css`
                  margin: 10px 0;
              `};
    color: ${({ theme }) => theme.colors.black};
    font-size: 16px;
    white-space: pre-wrap;
    overflow-wrap: break-word;

    p {
        margin: 0;
    }
`;

const MessageListItem = styled.li`
    margin-bottom: 15px;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    overflow: hidden;

    &:last-child {
        border-bottom: none;
    }
`;
